/**
 * The save format.
 *
 * A save is JSON. Tile planes are base64-encoded typed arrays; everything else is
 * ordinary objects, because GameState was designed to contain nothing else.
 *
 * The one subtlety worth understanding is CONTENT REMAPPING. Tile planes store
 * integer content ids, and those ids are just positions in the JSON content files.
 * If someone inserts a new terrain type at the top of terrain.json, every id shifts
 * and every existing save would silently become nonsense — grassland reading as
 * desert. So each save carries the id → key table it was written with, and loading
 * remaps ids through the keys. Reordering content is then a non-event; only
 * *deleting* content can break a save, and that fails loudly and names the key.
 */

import { loadContent, type Content } from '../../../content/schema/index.js';
import type { RngState } from '../rng/index.js';
import type {
  Civ,
  City,
  CivMemory,
  GameResult,
  GameState,
  History,
  Offer,
  Payment,
  ResourceGrant,
  Relation,
  RememberedCity,
  TileGrid,
  Unit,
} from '../state/types.js';
import { base64ToTypedArray, typedArrayToBase64 } from './base64.js';
import { CURRENT_SCHEMA, migrate, type RawSave } from './migrations.js';
import { maxSolventRate } from '../research/index.js';

export const SAVE_FORMAT = 'epoch-save';

/** The serialised shape of one civilisation's memory. */
interface RawMemory {
  terrain: string;
  feature: string;
  improvement: string;
  /** Absent in saves from before roads had their own layer (schema 10). */
  road?: string;
  cities: Array<[number, RememberedCity]>;
}

/** Tile planes and the typed-array kind each uses. Adding a plane means adding it here. */
const PLANES = [
  ['terrain', Uint8Array],
  ['feature', Uint8Array],
  ['resource', Uint8Array],
  ['improvement', Uint8Array],
  ['road', Uint8Array],
  ['elevation', Uint8Array],
  ['rivers', Uint8Array],
  ['owner', Int16Array],
  ['cultureOwner', Int16Array],
  ['workedBy', Int32Array],
] as const;

/** Which planes hold content ids, and which content table they index. */
const CONTENT_PLANES = [
  ['terrain', 'terrain'],
  ['feature', 'features'],
  ['resource', 'resources'],
  ['improvement', 'improvements'],
] as const;

/** Content tables a save carries a key list for. Wider than CONTENT_PLANES: unit types live on entities. */
type ContentTableName =
  | (typeof CONTENT_PLANES)[number][1]
  | 'units'
  | 'buildings'
  | 'technologies'
  | 'specialists'
  | 'personalities'
  | 'governments';

// ─── writing ───────────────────────────────────────────────────────────────────

export function serializeGame(state: GameState): string {
  const content = loadContent();

  const map: Record<string, unknown> = {
    width: state.map.width,
    height: state.map.height,
    wrapX: state.map.wrapX,
  };
  for (const [name] of PLANES) {
    map[name] = typedArrayToBase64(state.map[name]);
  }

  const save = {
    format: SAVE_FORMAT,
    schema: CURRENT_SCHEMA,
    meta: state.meta,
    // The id → key tables this save's planes were written against.
    content: {
      terrain: content.terrain.all.map((r) => r.key),
      features: content.features.all.map((r) => r.key),
      resources: content.resources.all.map((r) => r.key),
      improvements: content.improvements.all.map((r) => r.key),
      units: content.units.all.map((r) => r.key),
      buildings: content.buildings.all.map((r) => r.key),
      technologies: content.technologies.all.map((r) => r.key),
      specialists: content.specialists.all.map((r) => r.key),
      personalities: content.personalities.all.map((r) => r.key),
      governments: content.governments.all.map((r) => r.key),
    },
    map,
    // Per-civ knowledge, written in civ order so two identical states produce two
    // identical strings — which is the whole basis of hashState.
    vision: encodeVision(state),
    memory: encodeMemory(state),
    // Records serialise with unordered keys, so store entities as arrays. Saves stay
    // byte-identical for identical states, which is what makes hashing meaningful.
    civs: sortById(Object.values(state.civs)),
    cities: sortById(Object.values(state.cities)),
    units: sortById(Object.values(state.units)),
    relations: [...state.relations].sort((x, y) => x.a - y.a || x.b - y.b),
    payments: sortById(state.payments),
    grants: sortById(state.grants),
    offers: sortById(state.offers),
    result: state.result,
    history: { turns: state.history.turns, civs: sortedRecord(state.history.civs) },
    nextId: state.nextId,
    rng: state.rng,
  };

  return JSON.stringify(save);
}

/** A record keyed by civ id, rebuilt in ascending key order so it serialises the same every time. */
function sortedRecord<T>(record: Record<number, T>): Record<number, T> {
  const out: Record<number, T> = {};
  for (const key of Object.keys(record).map(Number).sort((a, b) => a - b)) out[key] = record[key]!;
  return out;
}

function sortById<T extends { id: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.id - b.id);
}

/**
 * Remembered contents, per civ.
 *
 * The sparse city record is written as a sorted array rather than an object: object
 * key order is only *usually* ascending for integer-like keys, and "usually" is not
 * good enough for something the state hash is computed over.
 */
function encodeMemory(state: GameState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const civId of Object.keys(state.memory)
    .map(Number)
    .sort((a, b) => a - b)) {
    const memory = state.memory[civId]!;
    out[String(civId)] = {
      terrain: typedArrayToBase64(memory.terrain),
      feature: typedArrayToBase64(memory.feature),
      improvement: typedArrayToBase64(memory.improvement),
      road: typedArrayToBase64(memory.road),
      cities: Object.keys(memory.cities)
        .map(Number)
        .sort((a, b) => a - b)
        .map((index) => [index, memory.cities[index]!]),
    };
  }
  return out;
}

function encodeVision(state: GameState): Record<string, string> {
  const out: Record<string, string> = {};
  for (const civId of Object.keys(state.vision)
    .map(Number)
    .sort((a, b) => a - b)) {
    out[String(civId)] = typedArrayToBase64(state.vision[civId]!);
  }
  return out;
}

// ─── reading ───────────────────────────────────────────────────────────────────

export function deserializeGame(json: string): GameState {
  let raw: RawSave;
  try {
    raw = JSON.parse(json) as RawSave;
  } catch {
    throw new Error('That file is not a valid Epoch save — it is not readable JSON.');
  }

  if (raw['format'] !== SAVE_FORMAT) {
    throw new Error('That file is not an Epoch save.');
  }

  const schema = raw['schema'];
  if (typeof schema !== 'number') throw new Error('Save is missing its schema version.');

  const save = migrate(raw, schema);
  const content = loadContent();

  const savedTables = save['content'] as Record<ContentTableName, string[]> | undefined;
  if (!savedTables) throw new Error('Save is missing its content tables and cannot be remapped.');

  const rawMap = save['map'] as Record<string, unknown>;
  const width = rawMap['width'] as number;
  const height = rawMap['height'] as number;
  const expected = width * height;

  const grid = {
    width,
    height,
    wrapX: rawMap['wrapX'] as boolean,
  } as TileGrid;

  // Before schema 10 a road was an improvement like any other; its own layer is
  // rebuilt from the improvements once the state exists (see below).
  const legacyRoads = typeof rawMap['road'] !== 'string';
  for (const [name, Kind] of PLANES) {
    const encoded = rawMap[name];
    if (name === 'road' && legacyRoads) {
      grid.road = new Uint8Array(expected);
      continue;
    }
    if (typeof encoded !== 'string') throw new Error(`Save is missing the "${name}" tile plane.`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- plane kinds are fixed by PLANES
    (grid as any)[name] = base64ToTypedArray(encoded, Kind as any, expected);
  }

  // Remap content ids through their keys, so reordered content files stay compatible.
  for (const [planeName, tableName] of CONTENT_PLANES) {
    const remap = buildRemap(savedTables[tableName], content, tableName);
    if (!remap) continue; // identity — nothing to do
    const plane = grid[planeName];
    for (let i = 0; i < plane.length; i++) plane[i] = remap[plane[i]!]!;
  }

  const units = save['units'] as Unit[];

  // Unit types are content ids too, and they live on entities rather than in a tile
  // plane — so they need the same remapping, or reordering units.json would turn
  // every scout in an old save into a warrior.
  const unitRemap = buildRemap(savedTables['units'], content, 'units');
  if (unitRemap) {
    for (const unit of units) unit.type = unitRemap[unit.type]!;
  }

  // Buildings a city has completed, and anything in its production queue, are content
  // ids living on entities rather than in a tile plane — so they need remapping too,
  // or reordering buildings.json would turn every granary in an old save into a market.
  const cities = save['cities'] as City[];
  const buildingRemap = buildRemap(savedTables['buildings'], content, 'buildings');
  if (buildingRemap || unitRemap) {
    for (const city of cities) {
      if (buildingRemap) {
        city.buildings = city.buildings.map((id) => buildingRemap[id]!).sort((a, b) => a - b);
      }
      city.queue = city.queue.map((item) =>
        item.kind === 'building'
          ? { kind: 'building', building: buildingRemap ? buildingRemap[item.building]! : item.building }
          : { kind: 'unit', type: unitRemap ? unitRemap[item.type]! : item.type },
      );
    }
  }

  // A city's chosen specialist is a content id too. NONE (Auto) is left alone.
  const specialistRemap = buildRemap(savedTables['specialists'], content, 'specialists');
  if (specialistRemap) {
    for (const city of cities) {
      if (city.specialist >= 0) city.specialist = specialistRemap[city.specialist]!;
    }
  }

  // What each civilisation knows and is studying: content ids on entities again. NONE
  // (-1) is not an id and is left alone.
  const civs = save['civs'] as Civ[];
  const personalityRemap = buildRemap(savedTables['personalities'], content, 'personalities');
  if (personalityRemap) {
    for (const civ of civs) if (civ.personality >= 0) civ.personality = personalityRemap[civ.personality]!;
  }
  const governmentRemap = buildRemap(savedTables['governments'], content, 'governments');
  if (governmentRemap) {
    for (const civ of civs) {
      if (civ.government >= 0) civ.government = governmentRemap[civ.government]!;
      if (civ.nextGovernment >= 0) civ.nextGovernment = governmentRemap[civ.nextGovernment]!;
    }
  }
  const techRemap = buildRemap(savedTables['technologies'], content, 'technologies');
  if (techRemap) {
    const remapTech = (id: number): number => (id < 0 ? id : techRemap[id]!);
    for (const civ of civs) {
      civ.techs = civ.techs.map(remapTech).sort((a, b) => a - b);
      civ.researching = remapTech(civ.researching);
      civ.researchGoal = remapTech(civ.researchGoal);
    }
  }

  // Offers awaiting an answer can name technologies and resources, which are content ids too.
  const offers = (save['offers'] as Offer[] | undefined) ?? [];
  const grants = (save['grants'] as ResourceGrant[] | undefined) ?? [];
  const resourceRemap = buildRemap(savedTables['resources'], content, 'resources');
  for (const offer of offers) {
    for (const item of [...offer.give, ...offer.get]) {
      if (item.kind === 'tech' && techRemap) item.tech = techRemap[item.tech]!;
      if (item.kind === 'resource' && resourceRemap) item.resource = resourceRemap[item.resource]!;
    }
  }
  if (resourceRemap) for (const grant of grants) grant.resource = resourceRemap[grant.resource]!;

  const vision: Record<number, Uint8Array> = {};
  const rawVision = (save['vision'] ?? {}) as Record<string, string>;
  for (const [civId, encoded] of Object.entries(rawVision)) {
    vision[Number(civId)] = base64ToTypedArray(encoded, Uint8Array, expected);
  }

  const memory: Record<number, CivMemory> = {};
  const rawMemory = (save['memory'] ?? {}) as Record<string, RawMemory>;
  for (const [civId, encoded] of Object.entries(rawMemory)) {
    const cities: Record<number, RememberedCity> = {};
    for (const [index, city] of encoded.cities) cities[index] = city;
    memory[Number(civId)] = {
      terrain: base64ToTypedArray(encoded.terrain, Uint8Array, expected),
      feature: base64ToTypedArray(encoded.feature, Uint8Array, expected),
      improvement: base64ToTypedArray(encoded.improvement, Uint8Array, expected),
      road: encoded.road ? base64ToTypedArray(encoded.road, Uint8Array, expected) : new Uint8Array(expected),
      cities,
    };
  }

  // Remembered terrain holds content ids too, so it remaps exactly like the live map.
  for (const [planeName, tableName] of CONTENT_PLANES) {
    if (planeName === 'resource') continue; // memory does not record resources
    const remap = buildRemap(savedTables[tableName], content, tableName);
    if (!remap) continue;
    for (const civMemory of Object.values(memory)) {
      const plane = civMemory[planeName as 'terrain' | 'feature' | 'improvement'];
      for (let i = 0; i < plane.length; i++) plane[i] = remap[plane[i]!]!;
    }
  }

  const state: GameState = {
    meta: save['meta'] as GameState['meta'],
    map: grid,
    civs: byId(civs),
    cities: byId(cities),
    units: byId(units),
    vision,
    memory,
    relations: (save['relations'] as Relation[] | undefined) ?? [],
    payments: (save['payments'] as Payment[] | undefined) ?? [],
    grants,
    offers,
    result: (save['result'] as GameResult | null | undefined) ?? null,
    history: (save['history'] as History | undefined) ?? { turns: [], civs: {} },
    nextId: save['nextId'] as number,
    rng: save['rng'] as Record<string, RngState>,
  };

  // A game saved before research existed has no science rate yet (the v4 migration
  // leaves it null). Give each civilisation the starting rate, lowered to whatever it
  // can afford — so an old game carries on with its books balanced, instead of finding
  // half its gold gone and its buildings being sold for a slider nobody had seen.
  // A game saved before roads had their own layer: lift every road out of the
  // improvement slot — the map's and each civilisation's memory of it — and put one
  // under every city, as a new game would have.
  if (legacyRoads && content.route) {
    const roadId = content.route.id;
    const lift = (improvement: Uint8Array, road: Uint8Array): void => {
      for (let i = 0; i < improvement.length; i++) {
        if (improvement[i] === roadId) {
          improvement[i] = 0;
          road[i] = 1;
        }
      }
    };
    lift(state.map.improvement, state.map.road);
    for (const civMemory of Object.values(state.memory)) lift(civMemory.improvement, civMemory.road);
    for (const city of Object.values(state.cities)) state.map.road[city.row * state.map.width + city.col] = 1;
  }

  // Before schema 11 a palace could be built anywhere, and the AI built one in every
  // city. Keep each civilisation's oldest (its first capital) and remove the rest.
  if (schema < 11) {
    const seat = content.buildings.all.find((b) => b.effects.isSeatOfGovernment === true);
    if (seat) {
      const kept = new Set<number>();
      for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
        if (!city.buildings.includes(seat.id)) continue;
        if (kept.has(city.owner)) city.buildings = city.buildings.filter((id) => id !== seat.id);
        else kept.add(city.owner);
      }
    }
  }

  // A game saved before governments (the v11 migration leaves -1) begins under the
  // starting one, as a new game would.
  for (const civ of Object.values(state.civs)) {
    if (civ.government === undefined || civ.government < 0) civ.government = content.governments.id(content.rules.government.start);
  }

  // A game saved before personalities (the v8 migration leaves -1) plays on with the one
  // each civilisation leans to.
  for (const civ of Object.values(state.civs)) {
    if (civ.personality === undefined || civ.personality < 0) {
      civ.personality = content.civilizations.get(civ.civType).personalityId;
    }
  }
  for (const civ of Object.values(state.civs)) {
    if (civ.researchRate === null || civ.researchRate === undefined) {
      civ.researchRate = Math.min(content.rules.research.startingRate, maxSolventRate(state, content, civ.id));
    }
  }
  state.meta.schema = CURRENT_SCHEMA;

  return state;
}

function byId<T extends { id: number }>(rows: T[]): Record<number, T> {
  const out: Record<number, T> = {};
  for (const row of rows) out[row.id] = row;
  return out;
}

/**
 * Build an old-id → new-id lookup, or null when the tables already match exactly
 * (the overwhelmingly common case, and worth the fast path on a 12,000-tile plane).
 */
function buildRemap(savedKeys: string[] | undefined, content: Content, tableName: ContentTableName): Uint8Array | null {
  if (!savedKeys) throw new Error(`Save is missing its "${tableName}" content table.`);

  const table = content[tableName];
  let identical = savedKeys.length === table.all.length;
  if (identical) {
    for (let i = 0; i < savedKeys.length; i++) {
      if (savedKeys[i] !== table.all[i]!.key) {
        identical = false;
        break;
      }
    }
  }
  if (identical) return null;

  const remap = new Uint8Array(256);
  for (let oldId = 0; oldId < savedKeys.length; oldId++) {
    const key = savedKeys[oldId]!;
    const row = table.byKey.get(key);
    if (!row) {
      throw new Error(
        `This save uses ${tableName} "${key}", which no longer exists in the game's content. ` +
          `It cannot be loaded by this build.`,
      );
    }
    remap[oldId] = row.id;
  }
  return remap;
}

// ─── hashing ───────────────────────────────────────────────────────────────────

/**
 * A stable 32-bit hash of the entire game state (FNV-1a over the canonical save).
 *
 * This is the workhorse of the golden and save-fuzz tests: two states are identical
 * if and only if their hashes match. Note that meta.createdAt is excluded — it is
 * wall-clock time and would make every hash unique.
 */
export function hashState(state: GameState): string {
  const canonical = serializeGame({
    ...state,
    meta: { ...state.meta, createdAt: 0 },
  });

  let hash = 2166136261;
  for (let i = 0; i < canonical.length; i++) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
